import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fetch live Google reviews from the backend.
 * Returns { configured, reviews, rating, userRatingCount, formattedAddress, googleMapsUri }.
 * `configured` is false when GOOGLE_PLACES_API_KEY / GOOGLE_PLACE_ID are not set on backend —
 * the caller should fall back to mocked reviews in that case.
 */
export function useGoogleReviews() {
  // Hydration-safe: prerendered pagina's injecteren de build-time reviews via
  // window.__PRERENDER_STATE__ zodat de eerste client-render identiek is aan
  // de prerendered HTML. Een verse fetch volgt alsnog na mount.
  const [data, setData] = useState(() => {
    const inj = typeof window !== "undefined" && window.__PRERENDER_STATE__ && window.__PRERENDER_STATE__.reviews;
    if (inj) {
      return {
        loading: false,
        configured: !!inj.configured,
        reviews: inj.reviews || [],
        rating: inj.rating ?? null,
        userRatingCount: inj.userRatingCount ?? null,
        formattedAddress: inj.formattedAddress || null,
        googleMapsUri: inj.googleMapsUri || null,
      };
    }
    return { loading: true, configured: false, reviews: [], rating: null, userRatingCount: null };
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/reviews/google`);
        const json = await res.json();
        // Capture voor de prerender (leest window.__PC_REVIEWS uit).
        window.__PC_REVIEWS = json;
        if (cancelled) return;
        setData({
          loading: false,
          configured: !!json.configured,
          reviews: json.reviews || [],
          rating: json.rating ?? null,
          userRatingCount: json.userRatingCount ?? null,
          formattedAddress: json.formattedAddress || null,
          googleMapsUri: json.googleMapsUri || null,
        });
      } catch {
        if (!cancelled) setData((d) => ({ ...d, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return data;
}

/**
 * Normalize a Google review item into the shape used by ReviewsSection.
 * Caller decides ID prefix to avoid clashes with mocked reviews.
 */
export function normalizeGoogleReview(r, idx) {
  return {
    id: `g-${idx}`,
    name: r.author?.name || "Google gebruiker",
    city: "",
    rating: Math.round(r.rating || 5),
    source: "Google",
    date: r.relativePublishTime || "",
    project: "",
    text: r.text || "",
    avatar: r.author?.photoUrl || null,
    profileUrl: r.author?.profileUrl || null,
  };
}
